import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getStudents, getStudentById, createStudent, updateStudent, deleteStudent } from '../../apis/students';
import { RootState } from '../store';

export interface Student {
    id: string;
    full_name: string;
    email: string;
    role: string;
    class_id?: string;
    class_ids?: string[];
    class_names?: string[];
    classes?: any[];
    teacher_id?: string;
    profile_picture_url?: string;
    total_credits?: number;
    deleted_at?: string | null;
    referal_code?: string;
    assignments_count?: number;
    submissions_count?: number;
    class_count?: number;
    date_of_birth?: string;
    grade?: string;
    profile?: any;
    organizations?: any[];
}

interface StudentState {
    students: Student[];
    total: number;
    page: number;
    size: number;
    pages: number;
    loading: boolean;
    error: string | null;
}

const initialState: StudentState = {
    students: [],
    total: 0,
    page: 1,
    size: 12,
    pages: 1,
    loading: false,
    error: null,
};

// Thunks
export const fetchStudents = createAsyncThunk(
    'students/fetchStudents',
    async (params: Record<string, any> = {}, { rejectWithValue }) => {
        try {
            const response = await getStudents(params);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchStudentById = createAsyncThunk(
    'students/fetchStudentById',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await getStudentById(id);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const addStudent = createAsyncThunk(
    'students/addStudent',
    async (data: Partial<Student>, { rejectWithValue }) => {
        try {
            const response = await createStudent(data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const editStudent = createAsyncThunk(
    'students/editStudent',
    async ({ id, data }: { id: string; data: Partial<Student> }, { rejectWithValue }) => {
        try {
            const response = await updateStudent(id, data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const removeStudent = createAsyncThunk(
    'students/removeStudent',
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteStudent(id);
            return id;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const studentSlice = createSlice({
    name: 'students',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchStudents.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStudents.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.students = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.size = action.payload.size;
                state.pages = action.payload.pages;
            })
            .addCase(fetchStudents.rejected, (state, action: any) => {
                state.loading = false;
                const pl = action.payload;
                state.error = pl?.detail
                    ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                    : (typeof pl === 'string' ? pl : 'Có lỗi xảy ra tải dữ liệu');
            })
            .addCase(addStudent.fulfilled, (state, action: PayloadAction<Student>) => {
                state.students.push(action.payload);
            })
            .addCase(editStudent.fulfilled, (state, action: PayloadAction<Student>) => {
                const index = state.students.findIndex((s) => s.id === action.payload.id);
                if (index !== -1) state.students[index] = action.payload;
            })
            .addCase(removeStudent.fulfilled, (state, action: PayloadAction<string>) => {
                state.students = state.students.filter((s) => s.id !== action.payload);
            })
            .addCase(fetchStudentById.fulfilled, (state, action: PayloadAction<Student>) => {
                const index = state.students.findIndex((s) => s.id === action.payload.id);
                if (index !== -1) {
                    state.students[index] = action.payload;
                } else {
                    state.students.push(action.payload);
                }
            });
    },
});

export const selectAllStudents = (state: RootState) => state.students.students;
export const selectStudentById = (state: RootState, id: string) =>
    state.students.students.find((s) => s.id === id);

export default studentSlice.reducer;
